import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'

const SECTIONS: Array<{ title: string, body: Array<string> }> = [
    {
        title: 'What we collect',
        body: [
            'When you send the contact form, we receive your name, your email address and, if you choose to share them, your brokerage or team name, your team size and your message. We do not ask for anything else.'
        ]
    },
    {
        title: 'Visitor statistics',
        body: [
            'We count visits with cookieless, privacy-friendly analytics. It records which pages are viewed, which site sent you here, and your country, browser and device type, all in aggregate. It sets no cookies and does not identify you personally.'
        ]
    },
    {
        title: 'How we use it',
        body: [
            'We use what you send only to reply to you and, if you asked for one, to set up a walkthrough. We do not sell or rent your details, and we do not add you to a mailing list.'
        ]
    },
    {
        title: 'Emails',
        body: [
            'The emails we send may include standard open and click measurement, which tells us whether a message was opened and which links were used. Turning off images in your email app blocks it.'
        ]
    },
    {
        title: 'How long we keep it',
        body: [
            'We keep contact requests for as long as we need them to follow up with you, then delete them. You can ask us to delete yours sooner at any time.'
        ]
    }
]

const Privacy: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Privacy'
                description='How Closewell handles the information you share through this website, in plain words.'
            />

            <article className='mx-auto max-w-2xl px-5 pt-14 pb-24 sm:pt-20'>
                <p className='text-sm text-mist'>
                    Last updated October 8, 2026
                </p>

                <h1 className='mt-4 animate-in font-heading text-[clamp(3rem,6.4vw,4.75rem)] leading-[0.95] tracking-[-0.03em] text-foreground duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] fade-in blur-in-8 slide-in-from-bottom-8'>
                    Privacy,
                    <span className='italic'> in plain words</span>
                </h1>

                <p className='mt-6 text-lg leading-relaxed text-muted-foreground'>
                    This notice covers this website: what we collect when you visit or write to us, and what we do with it.
                </p>

                <div className='mt-14 space-y-11'>
                    {SECTIONS.map(section => (
                        <section key={section.title} className='reveal' aria-labelledby={`privacy-${section.title.toLowerCase().replace(/\s+/g, '-')}`}>
                            <h2
                                id={`privacy-${section.title.toLowerCase().replace(/\s+/g, '-')}`}
                                className='font-heading text-[2rem] leading-tight tracking-[-0.01em] text-foreground'
                            >
                                {section.title}
                            </h2>

                            {section.body.map(paragraph => (
                                <p key={paragraph} className='mt-3 text-[16px] leading-relaxed text-foreground/75'>
                                    {paragraph}
                                </p>
                            ))}
                        </section>
                    ))}

                    <section className='reveal' aria-labelledby='privacy-your-choices'>
                        <h2 id='privacy-your-choices' className='font-heading text-[2rem] leading-tight tracking-[-0.01em] text-foreground'>
                            Your choices
                        </h2>

                        <p className='mt-3 text-[16px] leading-relaxed text-foreground/75'>
                            You can ask what we hold about you, ask us to correct it, or ask us to delete it. Send us a note through the{' '}
                            <Link href='/contact' className='text-foreground underline underline-offset-2 hover:text-primary'>
                                contact form
                            </Link>
                            {' '}and we will take care of it.
                        </p>
                    </section>
                </div>
            </article>
        </Layout>
    )
}

export default Privacy
